import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import MainSidebar from "components/MainSidebar";
import CrimeCategory from "./components/CrimeCategory";
import EvidenceMeter from "./components/EvidenceMeter";
import SkillProgressPanel from "./components/SkillProgressPanel";
import TerritoryBonusPanel from "./components/TerritoryBonusPanel";
import ConfirmCrimeModal from "./components/ConfirmCrimeModal";
import CrimeResultToast from "./components/CrimeResultToast";
import CrimeStatsBar from "./components/CrimeStatsBar";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { useRealtimeSubscription } from "../../hooks/useRealtimeSubscription";
import { useRealtimeCrime } from "../../hooks/useRealtimeCrime";
import { queueCrime } from "../../lib/indexedDBQueue";
import { useToast, TOAST_TYPE, TOAST_PRIORITY } from "../../hooks/useToast";
import { EmptyState } from 'components/SkeletonLoader';
import { logError } from '../../utils/logError';


const CATEGORY_ORDER = ['street', 'white-collar', 'trafficking', 'robbery', 'financial'];

const CrimeHub = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedCrime, setSelectedCrime] = useState(null);
  const [crimeResult, setCrimeResult] = useState(null);
  const [crimes, setCrimes] = useState([]);
  const [playerStats, setPlayerStats] = useState(null);
  const [evidenceTracker, setEvidenceTracker] = useState(null);
  const [cooldowns, setCooldowns] = useState({});
  const [crimeStats, setCrimeStats] = useState(null);
  const [masteryData, setMasteryData] = useState({});
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState(null);
  const [realtimeStatus, setRealtimeStatus] = useState('idle');
  const [pendingCrimeId, setPendingCrimeId] = useState(null);
  // Live activity feed
  const [activityFeed, setActivityFeed] = useState([]);
  // Live success rates per crime type
  const [liveSuccessRates, setLiveSuccessRates] = useState({});
  // Jail countdown
  const [jailCountdown, setJailCountdown] = useState(0);
  const cooldownTimerRef = useRef(null);
  const jailTimerRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const [crimesRes, statsRes, evidenceRes, cooldownsRes, attemptsRes, masteryRes] = await Promise.all([
        supabase?.from('crime_definitions')?.select('*')?.eq('is_active', true)?.order('required_level'),
        supabase?.from('player_stats')?.select('*')?.eq('user_id', user?.id)?.single(),
        supabase?.from('evidence_tracker')?.select('*')?.eq('user_id', user?.id)?.single(),
        supabase?.from('crime_cooldowns')?.select('*')?.eq('user_id', user?.id),
        supabase?.from('crime_attempts')?.select('success, reward')?.eq('user_id', user?.id)?.order('created_at', { ascending: false })?.limit(100),
        supabase?.from('crime_mastery')?.select('*')?.eq('user_id', user?.id),
      ]);

      if (crimesRes?.error) throw crimesRes?.error;

      const mappedCrimes = (crimesRes?.data || [])?.map(c => ({
        id: c?.id,
        crimeKey: c?.crime_key,
        name: c?.name,
        icon: c?.icon || 'Skull',
        difficulty: c?.difficulty,
        description: c?.description,
        minProfit: parseFloat(c?.min_reward),
        maxProfit: parseFloat(c?.max_reward),
        baseSuccessRate: Math.round((1 - parseFloat(c?.risk_probability)) * 100),
        skillBonus: Math.round(parseFloat(c?.skill_scaling_factor) * 100),
        energyCost: c?.energy_cost,
        jailTime: c?.jail_time_minutes,
        evidenceGain: Math.round(parseFloat(c?.evidence_accumulation_rate) * 100),
        xpReward: parseFloat(c?.xp_reward),
        requiredLevel: c?.required_level,
        cooldownSeconds: c?.cooldown_seconds,
        bailCost: parseFloat(c?.bail_cost),
        category: c?.category,
      }));
      setCrimes(mappedCrimes);

      if (!statsRes?.error) setPlayerStats(statsRes?.data);
      if (!evidenceRes?.error) setEvidenceTracker(evidenceRes?.data);

      const cdMap = {};
      (cooldownsRes?.data || [])?.forEach(cd => { cdMap[cd.crime_key] = new Date(cd.available_at); });
      setCooldowns(cdMap);

      const attempts = attemptsRes?.data || [];
      const succeeded = attempts?.filter(a => a?.success)?.length;
      const totalEarned = attempts?.filter(a => a?.success)?.reduce((s, a) => s + parseFloat(a?.reward || 0), 0);
      setCrimeStats({
        totalCrimes: attempts?.length,
        successful: succeeded,
        failed: attempts?.length - succeeded,
        totalEarned,
        timesJailed: attempts?.filter(a => !a?.success)?.length,
        successRate: attempts?.length > 0 ? Math.round((succeeded / attempts?.length) * 100) : 0,
      });

      // Build mastery map
      const masteryMap = {};
      (masteryRes?.data || [])?.forEach(m => { masteryMap[m.crime_key] = m; });
      setMasteryData(masteryMap);
    } catch (err) {
      console.error('Crime hub fetch error:', err);
      setError(err?.message || 'Failed to load crime data');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    cooldownTimerRef.current = setInterval(() => {
      const now = new Date();
      setCooldowns(prev => {
        const updated = { ...prev };
        let changed = false;
        Object.keys(updated)?.forEach(key => {
          if (updated?.[key] <= now) { delete updated?.[key]; changed = true; }
        });
        return changed ? updated : prev;
      });
    }, 1000);
    return () => clearInterval(cooldownTimerRef?.current);
  }, []);

  const { status: rtStatus } = useRealtimeSubscription(
    `player-stats-crime-${user?.id}`,
    { table: 'player_stats', event: 'UPDATE', filter: user?.id ? `user_id=eq.${user?.id}` : undefined },
    useCallback((payload) => { if (payload?.new) setPlayerStats(payload?.new); }, []),
    { enabled: !!user?.id }
  );

  // ── useRealtimeCrime callbacks ─────────────────────────────────
  const handleCrimeAttempt = useCallback((attempt) => {
    const entry = {
      id: attempt?.id || Date.now(),
      crimeName: attempt?.crime_key?.replace(/_/g, ' ') || 'Crime',
      success: attempt?.success,
      reward: parseFloat(attempt?.reward || 0),
      timestamp: attempt?.created_at || new Date()?.toISOString(),
    };
    setActivityFeed(prev => [entry, ...prev]?.slice(0, 20));
    setLiveSuccessRates(prev => {
      const key = attempt?.crime_key || 'unknown';
      const existing = prev?.[key] || { success: 0, total: 0 };
      const updated = {
        success: existing?.success + (attempt?.success ? 1 : 0),
        total: existing?.total + 1,
      };
      return { ...prev, [key]: updated };
    });
    setCrimeStats(prev => {
      if (!prev) return prev;
      const newTotal = prev?.totalCrimes + 1;
      const newSuccessful = prev?.successful + (attempt?.success ? 1 : 0);
      return {
        ...prev,
        totalCrimes: newTotal,
        successful: newSuccessful,
        failed: newTotal - newSuccessful,
        totalEarned: prev?.totalEarned + parseFloat(attempt?.reward || 0),
        successRate: Math.round((newSuccessful / newTotal) * 100),
      };
    });
  }, []);

  const handleStatsUpdate = useCallback((stats) => {
    setPlayerStats(stats);
  }, []);

  const handleEvidenceUpdate = useCallback((level) => {
    setEvidenceTracker(prev => prev ? { ...prev, evidence_level: level } : { evidence_level: level });
  }, []);

  const handleJailUpdate = useCallback((jailUntilVal) => {
    setPlayerStats(prev => prev ? { ...prev, jail_until: jailUntilVal } : prev);
  }, []);

  const { status: rtCrimeStatus } = useRealtimeCrime({
    userId: user?.id,
    onCrimeAttempt: handleCrimeAttempt,
    onStatsUpdate: handleStatsUpdate,
    onEvidenceUpdate: handleEvidenceUpdate,
    onJailUpdate: handleJailUpdate,
    enabled: !!user?.id,
  });

  useEffect(() => { setRealtimeStatus(rtStatus); }, [rtStatus]);
  useEffect(() => { setRealtimeStatus(rtCrimeStatus); }, [rtCrimeStatus]);

  const playerEnergy = playerStats?.energy || 0;
  const playerLevel = playerStats?.level || 1;
  const criminalSkill = parseFloat(playerStats?.criminal_skill || 0);
  const evidenceLevel = evidenceTracker?.evidence_level || parseFloat(playerStats?.evidence_level || 0);
  const isJailed = playerStats?.jail_until && new Date(playerStats.jail_until) > new Date();
  const jailUntil = playerStats?.jail_until ? new Date(playerStats.jail_until) : null;
  const bailCost = parseFloat(playerStats?.bail_cost || 0);

  // ── Jail countdown timer (placed after isJailed/jailUntil computed) ────────
  useEffect(() => {
    if (jailTimerRef?.current) clearInterval(jailTimerRef?.current);
    if (!isJailed || !jailUntil) { setJailCountdown(0); return; }
    const tick = () => {
      const remaining = Math.max(0, Math.round((jailUntil - new Date()) / 1000));
      setJailCountdown(remaining);
      if (remaining <= 0) {
        clearInterval(jailTimerRef?.current);
        fetchData();
      }
    };
    tick();
    jailTimerRef.current = setInterval(tick, 1000);
    return () => clearInterval(jailTimerRef?.current);
  }, [isJailed, jailUntil, fetchData]);

  const getCooldownRemaining = (crimeKey) => {
    const avail = cooldowns?.[crimeKey];
    if (!avail) return 0;
    return Math.max(0, Math.round((avail - new Date()) / 1000));
  };

  // Group crimes by category
  const crimesByCategory = CATEGORY_ORDER?.reduce((acc, cat) => {
    const catCrimes = crimes
      ?.filter(c => c?.category === cat)
      ?.map(c => ({ ...c, cooldownRemaining: getCooldownRemaining(c?.crimeKey) }));
    if (catCrimes?.length > 0) acc[cat] = catCrimes;
    return acc;
  }, {});

  // Filtered view
  const filteredCrimesByCategory = selectedCategory === 'all'
    ? crimesByCategory
    : Object.fromEntries(
        Object.entries(crimesByCategory)?.filter(([cat]) => cat === selectedCategory)
      );

  const handleExecute = useCallback((crime) => {
    if (isJailed) {
      setCrimeResult({ success: false, message: 'You are currently in jail.', jailTime: 0 });
      return;
    }
    setSelectedCrime(crime);
  }, [isJailed]);

  const handleConfirm = useCallback(async (crime) => {
    if (!user?.id || executing) return;

    // Validate before attempting
    if (isJailed) {
      setCrimeResult({ success: false, message: 'Cannot attempt crime while in jail.', jailTime: 0 });
      showToast('Cannot attempt crime while in jail', TOAST_TYPE?.ERROR, TOAST_PRIORITY?.HIGH);
      return;
    }
    if (playerEnergy < crime?.energyCost) {
      const needed = crime?.energyCost - playerEnergy;
      setCrimeResult({ success: false, message: `Insufficient energy — need ${needed} more energy.`, jailTime: 0 });
      showToast(`Insufficient energy — need ${needed} more`, TOAST_TYPE?.ERROR, TOAST_PRIORITY?.HIGH);
      return;
    }
    const cooldownRemaining = getCooldownRemaining(crime?.crimeKey);
    if (cooldownRemaining > 0) {
      setCrimeResult({ success: false, message: `Crime on cooldown — ${cooldownRemaining}s remaining.`, jailTime: 0 });
      return;
    }

    setSelectedCrime(null);
    setExecuting(true);

    // Optimistic UI: immediately deduct energy and show confirming state
    const prevStats = { ...playerStats };
    setPlayerStats(prev => prev ? { ...prev, energy: Math.max(0, (prev?.energy || 0) - crime?.energyCost) } : prev);
    setPendingCrimeId(crime?.id);

    try {
      const { data, error } = await supabase?.rpc('attempt_crime', {
        p_user_id: user?.id,
        p_crime_key: crime?.crimeKey,
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.success) {
        const profit = parseFloat(data?.reward || 0);
        setCrimeResult({
          success: true,
          message: `${crime?.name} executed successfully. Funds transferred.`,
          profit,
          xp: parseFloat(data?.xp_gained || 0),
        });
        showToast(
          `💰 Crime successful! Earned $${profit?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          TOAST_TYPE?.CRIME_SUCCESS,
          TOAST_PRIORITY?.NORMAL
        );
        const currentMastery = masteryData?.[crime?.crimeKey] || { mastery_level: 0, successful_attempts: 0, total_attempts: 0, total_earned: 0 };
        const newSuccessful = (currentMastery?.successful_attempts || 0) + 1;
        const newMasteryLevel = Math.floor(newSuccessful / 10);
        setMasteryData(prev => ({
          ...prev,
          [crime?.crimeKey]: {
            ...currentMastery,
            successful_attempts: newSuccessful,
            total_attempts: (currentMastery?.total_attempts || 0) + 1,
            total_earned: (currentMastery?.total_earned || 0) + profit,
            mastery_level: newMasteryLevel,
          },
        }));
      } else {
        // Rollback energy on jail
        setPlayerStats(prevStats);
        const jailMins = data?.jail_minutes || crime?.jailTime;
        const bail = data?.bail_cost || crime?.bailCost;
        setCrimeResult({
          success: false,
          message: `${crime?.name} failed. You were caught by authorities.`,
          jailTime: jailMins,
          bailCost: bail,
        });
        showToast(
          `🚔 Caught! Jailed for ${jailMins} minutes. Bail: $${parseFloat(bail || 0)?.toLocaleString()}`,
          TOAST_TYPE?.CRIME_JAIL,
          TOAST_PRIORITY?.HIGH
        );
      }
      fetchData();
    } catch (err) {
      // Rollback optimistic energy deduction
      setPlayerStats(prevStats);
      console.error('Crime execution error:', err);
      logError({ error: err, componentName: 'CrimeHub', actionName: 'handleConfirm', screenName: 'crime-hub', userId: user?.id, context: { crimeKey: crime?.crimeKey } });

      if (!navigator.onLine) {
        await queueCrime({ crime_id: crime?.id, crime_key: crime?.crimeKey });
        setCrimeResult({ success: false, message: 'Offline — crime queued for retry when connection restores.', jailTime: 0 });
        showToast('Offline — crime queued for retry', TOAST_TYPE?.WARNING, TOAST_PRIORITY?.HIGH);
      } else {
        setCrimeResult({ success: false, message: err?.message || 'Crime execution failed.', jailTime: 0 });
        showToast(`Crime failed: ${err?.message || 'Unknown error'}`, TOAST_TYPE?.ERROR, TOAST_PRIORITY?.HIGH);
      }
    } finally {
      setExecuting(false);
      setPendingCrimeId(null);
    }
  }, [user?.id, executing, masteryData, fetchData, showToast, isJailed, playerEnergy, playerStats, getCooldownRemaining]);

  const handleBailOut = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { error } = await supabase?.rpc('bail_out', { p_user_id: user?.id });
      if (error) throw error;
      fetchData();
    } catch (err) {
      console.error('Bail out error:', err);
    }
  }, [user?.id, fetchData]);

  const categoryTabs = [
    { id: 'all', label: 'All', icon: 'Grid3X3' },
    { id: 'street', label: 'Petty', icon: 'MapPin' },
    { id: 'white-collar', label: 'White Collar', icon: 'Briefcase' },
    { id: 'trafficking', label: 'Organized', icon: 'Package' },
    { id: 'robbery', label: 'Robbery', icon: 'Landmark' },
    { id: 'financial', label: 'High Stakes', icon: 'TrendingUp' },
  ];

  return (
    <div className="min-h-screen bg-background flex">
      <MainSidebar
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(c => !c)}
        userRole="player"
      />
      <div className={`flex-1 flex flex-col min-h-screen transition-all duration-250 pb-16 lg:pb-0 ${
        sidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-[280px]'
      }`}>
        {/* Header */}
        <header className="sticky top-0 z-40 bg-secondary/95 backdrop-blur border-b border-border px-4 md:px-6 py-3 flex items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Icon name="Skull" size={20} color="var(--color-error)" />
            <h1 className="font-heading text-base md:text-lg font-bold text-foreground">Crime Hub</h1>
            <span className="hidden md:flex items-center gap-1 font-caption text-xs text-muted-foreground bg-secondary border border-border px-2 py-1 rounded-full">
              <span className={`w-1.5 h-1.5 rounded-full ${
                realtimeStatus === 'connected' ? 'bg-emerald-400' : 'bg-amber-400 animate-pulse'
              }`} />
              {realtimeStatus === 'connected' ? 'Live' : 'Connecting...'}
            </span>
          </div>
          <button onClick={() => fetchData()} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-caption text-xs text-muted-foreground hover:text-primary hover:bg-primary/10 border border-border transition-all min-h-[36px]">
            <Icon name="RefreshCw" size={13} color="currentColor" />
            Refresh
          </button>
        </header>

        <div className="flex-1 flex flex-col lg:flex-row gap-0 overflow-hidden">
          {/* Main content */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6">
            {/* Stats Bar */}
            {crimeStats && <CrimeStatsBar stats={crimeStats} className="mb-4" />}

            {/* Jail Banner with countdown */}
            {isJailed && jailUntil && (
              <div className="mb-4 p-4 rounded-xl border border-red-500/30 bg-red-500/10">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <Icon name="Lock" size={20} color="#ef4444" />
                    <div>
                      <div className="font-heading text-sm font-bold text-red-400">Currently Jailed</div>
                      <div className="font-caption text-xs text-muted-foreground">
                        {jailCountdown > 0 ? (
                          <span>Released in <span className="font-mono font-bold text-red-300">{Math.floor(jailCountdown / 60)}:{String(jailCountdown % 60)?.padStart(2, '0')}</span></span>
                        ) : (
                          <span>Released at {jailUntil?.toLocaleTimeString()}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {bailCost > 0 && (
                    <Button variant="danger" size="sm" onClick={handleBailOut}>
                      Bail Out (${bailCost?.toLocaleString()})
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="mb-4 p-3 rounded-lg border border-red-500/30 bg-red-500/10 flex items-center gap-2">
                <Icon name="AlertCircle" size={16} color="#ef4444" />
                <span className="text-sm text-red-400">{error}</span>
                <button onClick={fetchData} className="ml-auto text-xs text-primary hover:underline">Retry</button>
              </div>
            )}

            {/* Category Tabs */}
            <div className="flex gap-2 mb-5 overflow-x-auto pb-1 scrollbar-hide">
              {categoryTabs?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setSelectedCategory(tab?.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg font-caption text-xs font-medium whitespace-nowrap transition-all min-h-[36px] flex-shrink-0 ${
                    selectedCategory === tab?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card border border-border text-muted-foreground hover:text-primary hover:border-primary/30'
                  }`}
                >
                  <Icon name={tab?.icon} size={13} color="currentColor" />
                  {tab?.label}
                </button>
              ))}
            </div>

            {/* Loading */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {Array.from({ length: 8 })?.map((_, i) => (
                  <div key={i} className="h-64 rounded-xl bg-card border border-border animate-pulse" />
                ))}
              </div>
            ) : Object.keys(filteredCrimesByCategory)?.length === 0 ? (
              <EmptyState
                icon={<Icon name="Skull" size={24} color="var(--color-muted-foreground)" />}
                title="No crimes available"
                description="Select a category to view available crimes."
                actionLabel="View All"
                action={() => setSelectedCategory('all')}
              />
            ) : (
              Object.entries(filteredCrimesByCategory)?.map(([category, categoryCrimes]) => (
                <CrimeCategory
                  key={category}
                  category={category}
                  crimes={categoryCrimes}
                  onExecute={handleExecute}
                  territoryBonus={0}
                  masteryData={masteryData}
                  liveSuccessRates={liveSuccessRates}
                />
              ))
            )}

            {/* Live Activity Feed */}
            {activityFeed?.length > 0 && (
              <div className="mt-6 rounded-xl border p-4" style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" style={{ boxShadow: '0 0 6px rgba(52,211,153,0.8)' }} />
                  <span className="font-heading text-xs font-semibold text-foreground">Live Activity Feed</span>
                  <span className="ml-auto font-caption text-xs text-muted-foreground">{activityFeed?.length} recent</span>
                </div>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {activityFeed?.map((entry, i) => (
                    <div
                      key={entry?.id}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-all"
                      style={{
                        background: i === 0 ? (entry?.success ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)') : 'rgba(255,255,255,0.02)',
                        borderLeft: i === 0 ? `2px solid ${entry?.success ? 'var(--color-success)' : 'var(--color-error)'}` : '2px solid transparent',
                        animation: i === 0 ? 'slideInRight 0.3s ease' : 'none',
                      }}
                    >
                      <Icon
                        name={entry?.success ? 'CheckCircle' : 'XCircle'}
                        size={12}
                        color={entry?.success ? 'var(--color-success)' : 'var(--color-error)'}
                      />
                      <span className="font-caption capitalize text-foreground flex-1">{entry?.crimeName}</span>
                      {entry?.success && entry?.reward > 0 && (
                        <span className="font-heading text-xs font-semibold" style={{ color: 'var(--color-success)' }}>
                          +${entry?.reward?.toLocaleString()}
                        </span>
                      )}
                      {!entry?.success && (
                        <span className="font-caption text-xs" style={{ color: 'var(--color-error)' }}>Failed</span>
                      )}
                      <span className="font-caption text-xs text-muted-foreground ml-1">
                        {new Date(entry.timestamp)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right sidebar */}
          <div className="hidden lg:flex flex-col gap-4 w-72 xl:w-80 p-4 border-l border-border overflow-y-auto">
            <EvidenceMeter evidenceLevel={evidenceLevel} />
            <SkillProgressPanel skill={criminalSkill} level={playerLevel} energy={playerEnergy} />
            <TerritoryBonusPanel />
          </div>
        </div>
      </div>
      {/* Modals */}
      {selectedCrime && (
        <ConfirmCrimeModal
          crime={selectedCrime}
          playerStats={playerStats}
          evidenceLevel={evidenceLevel}
          onConfirm={handleConfirm}
          onCancel={() => setSelectedCrime(null)}
          executing={executing}
        />
      )}
      {crimeResult && (
        <CrimeResultToast
          result={crimeResult}
          onClose={() => setCrimeResult(null)}
          onDismiss={() => setCrimeResult(null)}
        />
      )}
    </div>
  );
};

export default CrimeHub;